import React, { useState, useEffect } from 'react';
import { MessageCircle, Search, ArrowLeft, Edit, Clock, Check, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Contact } from '../types';
import { contactService } from '../services/contactService';

interface MessengerPageProps {
    currentUser: Contact;
    contacts: Contact[];
    onBack: () => void;
    onOpenChat: (contact: Contact) => void;
}

interface Conversation {
    contactId: string;
    contactName: string;
    contactAvatar: string;
    lastMessage: string;
    time: Date;
    unread: boolean;
}

const MessengerPage: React.FC<MessengerPageProps> = ({ currentUser, contacts, onBack, onOpenChat }) => {
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadConversations();
        const sub = contactService.subscribeToMessages(currentUser.id, () => {
            loadConversations();
        });
        return () => { sub.unsubscribe(); }
    }, [currentUser]);

    const loadConversations = async () => {
        setIsLoading(true);
        try {
            const data = await contactService.getRecentConversations(currentUser.id);
            setConversations(data);
        } catch (e) {
            console.error("Failed to load inbox", e);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredConversations = conversations.filter(c =>
        c.contactName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatTime = (date: Date) => {
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const days = diff / (1000 * 3600 * 24);

        if (days < 1) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        if (days < 7) return date.toLocaleDateString([], { weekday: 'short' });
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };

    return (
        <div className="fixed inset-0 z-[50] bg-[#020817] text-white flex flex-col font-sans animate-fade-in">
            {/* Background Texture */}
            <div className="absolute inset-0 opacity-20 pointer-events-none bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />

            {/* Header */}
            <header className="relative z-10 flex-shrink-0 px-4 py-4 flex items-center justify-between border-b border-white/5 bg-[#0a0f18]/80 backdrop-blur-md">
                <div className="flex items-center gap-3">
                    <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-white/10 transition-colors group">
                        <ArrowLeft className="w-6 h-6 text-gray-400 group-hover:text-white" />
                    </button>
                    <div>
                        <h1 className="text-xl font-display font-bold tracking-wide">Secure Comm</h1>
                        <p className="text-[10px] text-[var(--neon-cyan)] font-mono tracking-widest uppercase flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--neon-cyan)] animate-pulse" />
                            Encrypted Uplink
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button className="p-2 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 transition-colors text-white">
                        <Edit className="w-5 h-5" />
                    </button>
                </div>
            </header>

            {/* Content */}
            <div className="flex-1 overflow-y-auto relative z-10">
                {/* Search */}
                <div className="px-4 py-3 sticky top-0 z-20 bg-[#020817]/90 backdrop-blur-xl border-b border-white/5">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Decipher contact..."
                            className="w-full pl-11 pr-4 py-3 bg-[#0d1f3c]/50 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:border-blue-500/50 focus:outline-none transition-all font-mono"
                        />
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-40 gap-4">
                        <div className="w-8 h-8 border-2 border-[var(--neon-cyan)] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-xs font-mono text-gray-500 animate-pulse">DECRYPTING MESSAGES...</p>
                    </div>
                ) : filteredConversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 px-10 text-center text-gray-500">
                        <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mb-4 border border-white/5">
                            <MessageCircle className="w-10 h-10 opacity-30" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-300 mb-2 font-display uppercase tracking-widest">No Transmissions</h3>
                        <p className="text-sm font-mono opacity-60">Initiate secure handshake with contacts to begin.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-white/5">
                        {filteredConversations.map((conv, i) => {
                            // Find full contact object to pass to onOpenChat (Mock pickup if missing)
                            const fullContact = contacts.find(c => c.id === conv.contactId) || {
                                id: conv.contactId,
                                name: conv.contactName,
                                avatarUrl: conv.contactAvatar,
                                status: 'OFFLINE' as any,
                                role: 'Agent',
                                battery: 0,
                                locationName: 'Unknown',
                                coordinates: { lat: 0, lng: 0 },
                                lastSeen: '',
                                isTrackable: false
                            };

                            return (
                                <motion.div
                                    key={conv.contactId}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    onClick={() => onOpenChat(fullContact)}
                                    className="flex items-center gap-4 p-4 hover:bg-white/5 cursor-pointer transition-colors group relative overflow-hidden"
                                >
                                    <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                                    <div className="relative">
                                        <div className="w-14 h-14 rounded-full p-0.5 border border-white/10 group-hover:border-blue-500/50 transition-colors">
                                            <img
                                                src={conv.contactAvatar || `https://api.dicebear.com/9.x/avataaars/svg?seed=${conv.contactId}`}
                                                alt={conv.contactName}
                                                className="w-full h-full rounded-full bg-gray-900 object-cover"
                                            />
                                        </div>
                                        {/* Status Dot */}
                                        <div className="absolute bottom-1 right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-[#020817]" />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-baseline mb-1">
                                            <h3 className="font-bold text-white truncate text-base font-display tracking-wide group-hover:text-blue-400 transition-colors">{conv.contactName}</h3>
                                            <span className="text-[10px] text-gray-500 whitespace-nowrap ml-2 font-mono flex items-center gap-1">
                                                {formatTime(conv.time)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {conv.lastMessage.includes('::PULSE') ? (
                                                <span className="text-[var(--neon-cyan)] font-mono text-[10px] tracking-widest uppercase flex items-center gap-1 bg-[var(--neon-cyan)]/10 px-1.5 py-0.5 rounded">
                                                    <Sparkles className="w-3 h-3" /> PING RECEIVED
                                                </span>
                                            ) : conv.lastMessage.startsWith('📍') ? (
                                                <span className="text-blue-400 font-bold text-xs flex items-center gap-1">
                                                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse" />
                                                    Coords Attached
                                                </span>
                                            ) : (
                                                <p className={`text-sm truncate ${conv.unread ? 'text-white font-medium' : 'text-gray-400 group-hover:text-gray-300'}`}>
                                                    {conv.lastMessage}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {conv.unread && (
                                        <div className="w-2 h-2 bg-[var(--neon-cyan)] rounded-full shadow-[0_0_10px_var(--neon-cyan)] animate-pulse"></div>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessengerPage;
